import React, { useContext } from 'react';
import type { IntlShape, WithIntlProps } from 'react-intl';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';

import { TEMPLATE_PREVIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import { PreviewMessage } from './PreviewMessage.tsx';
import blogPostIcon from './img/blogPostIcon.svg';

export const i18n: {
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	message: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	iconDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	title: {
		id: 'template-preview.custom.blog-post.title',
		defaultMessage: 'Bring your blog to life',
		description:
			'The text is shown as the main heading in the custom blog post template preview panel, encouraging users to create a blog post using the selected template.',
	},
	message: {
		id: 'template-preview.custom.blog-post.message',
		defaultMessage:
			'You control what goes in a blog post, so {lineBreak} let your imagination run wild.',
		description:
			'The text is shown as a descriptive message below the heading in the custom blog post template preview panel. The placeholder {lineBreak} will be substituted with a line break element to split the sentence across two lines.',
	},
	iconDescription: {
		id: 'template-preview.custom.blog-post.icon.description',
		defaultMessage: 'Blog post',
		description:
			"The text is the accessible description for an icon displayed in the custom blog post template preview panel, used by screen readers to convey the icon's meaning.",
	},
});

type Props = {
	intl: IntlShape;
};

export const BLOG_POST_ITEM_MODULE_COMPLETE_KEY =
	'com.atlassian.confluence.plugins.confluence-create-content-plugin:create-blog-post';

export const CustomBlogPostPreview: React.FC<WithIntlProps<Props>> & {
	WrappedComponent: React.ComponentType<Props>;
} = injectIntl(({ intl }: Props) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	// We're not using <ExperienceSuccess /> because this component isn't actually de-mounting properly, the current logic just hides it.
	experienceTracker.succeed({
		name: TEMPLATE_PREVIEW_EXPERIENCE,
	});

	return (
		<PreviewMessage
			title={intl.formatMessage(i18n.title)}
			message={<FormattedMessage {...i18n.message} values={{ lineBreak: <br /> }} />}
			icon={blogPostIcon}
			iconDescription={intl.formatMessage(i18n.iconDescription)}
			iconSize="medium"
			testId="template-preview-custom-blog-post-preview"
		/>
	);
});
